import { Modal } from "antd";
import { useLoading } from "components/GlobalLoading";
import InputSearch from "components/Input/InputSearch";
import useDetalheUnidadeUnificada from "core/features/unidade-unificada/hooks/useDetalheUnidadeUnificada";
import useSaveUnidadeUnificada from "core/features/unidade-unificada/hooks/useSaveUnidadeUnificada";
import { UnidadeUnificadaForm } from "core/features/unidade-unificada/typings";
import { TipoEntidadeEnum } from "core/resources/enums/conta";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import FooterModal from "ui/components/Button/FooterModal";
import Field from "ui/components/Input/Field";
import SelectPessoas from "ui/components/Input/SelectPessoas";
import { formatBlocoQuadra } from "infra/helpers/formatBlocoQuadra";
import { Flex, Box, Text, Checkbox } from "@chakra-ui/react";
import colors from "core/resources/theme/colors";
import { TipoUnidadePessoaEnum } from "core/features/unidades/typings/unidade";
import useUnidadesParaUnificacao from "core/features/unidades/useUnidadesParaUnificacao";

interface ModalUnidadeUnificadaProps {
  visible: boolean;
  onClose: () => any;
  onSaved?: () => any;
  editId?: string;
}

const defaultValues: Partial<UnidadeUnificadaForm> = {
  responsavelId: undefined,
  unidadesIds: [],
};

function ModalUnidadeUnificada({
  visible,
  onClose,
  onSaved,
  editId,
}: ModalUnidadeUnificadaProps) {
  const [search, setSearch] = useState("");
  const { mutate: saveUnidadeUnificada, isLoading: isLoadingSave } =
    useSaveUnidadeUnificada(editId);

  const { control, setValue, watch, formState, reset, handleSubmit } =
    useForm<UnidadeUnificadaForm>({
      defaultValues,
      mode: "all",
    });

  const form = watch();

  const onSubmit = (data: UnidadeUnificadaForm) => {
    saveUnidadeUnificada(data, {
      onSuccess: onSaved,
    });
  };

  const {
    data: detalhe,
    isLoading: isLoadingDetalhe,
    isFetching: isFetchingDetalhe,
  } = useDetalheUnidadeUnificada(editId);

  const {
    data: unidades,
    isLoading: isLoadingUnidades,
    isFetching: isFetchingUnidades,
  } = useUnidadesParaUnificacao(
    undefined,
    {
      limit: 9999,
      responsavelId: form.responsavelId,
    },
    !!form.responsavelId
  );

  useEffect(() => {
    if (!visible) {
      reset(defaultValues);
    }
  }, [reset, visible]);

  useEffect(() => {
    if (detalhe) {
      reset({
        id: detalhe.id,
        responsavelId: detalhe.responsavel.id,
        unidadesIds: detalhe.unidades.map((unidade) => unidade.id),
      });
    }
  }, [detalhe, reset]);

  useLoading(
    isLoadingDetalhe ||
      isFetchingDetalhe ||
      isLoadingUnidades ||
      isFetchingUnidades
  );

  const filterUnidades =
    unidades?.items.filter((unidade) =>
      search
        ? formatBlocoQuadra(unidade.numero, unidade.blocoQuadra?.nome)
            .toLowerCase()
            .includes(search.toLowerCase())
        : true
    ) || [];

  const isAllSelected =
    form.unidadesIds.length &&
    unidades?.items.length === form.unidadesIds.length;

  const isIndeterminate =
    form.unidadesIds.length &&
    unidades?.items.length &&
    form.unidadesIds.length < unidades?.items.length;
  return (
    <Modal
      visible={visible}
      title={editId ? "Editar unificação" : "Criar unificação"}
      onCancel={onClose}
      width={"600px"}
      destroyOnClose
      footer={
        <FooterModal
          primaryButtonText="Salvar"
          isDisabled={
            !formState.isValid ||
            isLoadingSave ||
            !form.responsavelId ||
            form.unidadesIds.length < 2
          }
          onClose={onClose}
          isLoadingSave={isLoadingSave}
          onSubmit={handleSubmit(onSubmit)}
          showRegisterAnother={false}
          registerAnother={false}
        />
      }
    >
      <Box px={2}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box maxW={300} mb={4}>
            <Field
              control={control}
              name="responsavelId"
              label="Responsável"
              rules={{ required: true }}
              render={({ field, isInvalid }) => (
                <SelectPessoas
                  showSearch
                  tenantId={null}
                  isInvalid={isInvalid}
                  incluirPessoasComUnidade
                  tipoUnidadePessoa={[
                    TipoUnidadePessoaEnum.Proprietario,
                    TipoUnidadePessoaEnum.Locatario,
                  ]}
                  tipoEntidades={[TipoEntidadeEnum.Condomino]}
                  {...field}
                  allowClear
                  onClear={() => field.onChange("")}
                  onChange={(v: string) => {
                    field.onChange(v);
                    setValue("unidadesIds", []);
                  }}
                />
              )}
            />
          </Box>
          {form.responsavelId ? (
            <>
              <Box mb={4}>
                <InputSearch
                  defaultValue={search}
                  onSearch={(v) => setSearch(v ?? "")}
                  placeholder="Pesquisar"
                  maxW="100%"
                />
              </Box>

              <Flex
                alignItems="center"
                borderBottomWidth={1}
                borderBottomColor={colors.gray50}
                paddingBottom={2}
              >
                <Checkbox
                  isChecked={!!isAllSelected}
                  isIndeterminate={!!isIndeterminate}
                  onChange={() =>
                    !isAllSelected
                      ? setValue(
                          "unidadesIds",
                          unidades!.items.map((unidade) => unidade.id)
                        )
                      : setValue("unidadesIds", [])
                  }
                />

                <Text ml={4} noOfLines={1} fontSize={16} fontWeight="bold">
                  Unidade
                </Text>
              </Flex>

              <Flex
                flexDirection="column"
                maxHeight={400}
                overflowY="auto"
                className="custom-scroll-bar"
              >
                {filterUnidades.map((unidade) => {
                  const isChecked = form.unidadesIds.includes(unidade.id);

                  return (
                    <Flex
                      alignItems="center"
                      key={unidade.id}
                      borderBottomWidth={1}
                      borderBottomColor={colors.gray50}
                    >
                      <Checkbox
                        isChecked={isChecked}
                        onChange={() =>
                          isChecked
                            ? setValue(
                                "unidadesIds",
                                form.unidadesIds.filter(
                                  (unidadeId) => unidadeId !== unidade.id
                                )
                              )
                            : setValue(
                                "unidadesIds",
                                form.unidadesIds.concat(unidade.id)
                              )
                        }
                      />

                      <Text ml={4} noOfLines={1}>
                        {formatBlocoQuadra(
                          unidade.numero,
                          unidade.blocoQuadra?.nome
                        )}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
            </>
          ) : null}
        </form>
      </Box>
    </Modal>
  );
}

export default ModalUnidadeUnificada;
